import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';
import LanguageMenu from '../LanguageMenu/LanguageMenu';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink id="signup-link" name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink id="login-link" name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const isSearchPage = page =>
  page === 'SearchPage' || page === 'SearchPageWithListingType';

const TopbarNavLink = ({ name, params, currentPage, pageName, children }) => {
  const cmsPageId = name === 'CMSPage' ? params?.pageId : null;
  const isCurrent =
    (cmsPageId && currentPage === `CMSPage:${cmsPageId}`) ||
    (pageName === 'SearchPage' && isSearchPage(currentPage)) ||
    currentPage === pageName;

  return (
    <NamedLink
      name={name}
      params={params}
      className={classNames(css.navLink, { [css.navLinkCurrent]: isCurrent })}
    >
      {children}
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      id="inbox-link"
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: inboxTab }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};


const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, showCreateListingsLink, intl }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isServiceProvider = userType === 'service_provider' || userType === 'serviceprovider';
  const isAdmin = userType === 'admin';

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink && !isServiceProvider ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {showCreateListingsLink && (isServiceProvider || isAdmin) ? (
          <MenuItem key="NewListingPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('NewListingPage'))}
              name="NewListingPage"
            >
              <span className={css.menuItemBorder} />
              {isServiceProvider ? (
                <FormattedMessage id="TopbarDesktop.syncCalendar" />
              ) : (
                <FormattedMessage id="TopbarDesktop.addExperience" />
              )}
            </NamedLink>
          </MenuItem>
        ) : null}
        {isAdmin ? (
          <MenuItem key="InviteProviderPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('InviteProviderPage'))}
              name="InviteProviderPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.inviteProviderLink" />
            </NamedLink>
          </MenuItem>
        ) : null}

        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        {isAdmin ? (
          <MenuItem key="MeetingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('MeetingsPage'))}
              name="MeetingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.meetings" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {/* {isAdmin ? (
          <MenuItem key="RezdyBookingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('RezdyBookingsPage'))}
              name="RezdyBookingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.rezdyBookings" />
            </NamedLink>
          </MenuItem>
        ) : null} */}
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showCreateListingsLink
 * @returns {JSX.Element} desktop topbar
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    currentUser,
    currentPage,
    rootClassName,
    intl,
    isAuthenticated,
    onLogout,
    showCreateListingsLink,
    showManageListingsLink,
    notificationCount = 0,
    inboxTab,
    onSearchClick,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;

  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className, {
    [css.rootSearchPage]: isSearchPage(currentPage),
  });

  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isServiceProvider = userType === 'service_provider';
  const isAdmin = userType === 'admin';

  const inboxLinkMaybe =
    authenticatedOnClientSide && !isServiceProvider ? (
      <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
    ) : null;

  const createListingLinkMaybe =
    authenticatedOnClientSide && showCreateListingsLink && (isServiceProvider || isAdmin) ? (
      <NamedLink name="NewListingPage" className={css.topbarLink}>
        <span className={css.topbarLinkLabel}>
          {isServiceProvider ? (
            <FormattedMessage id="TopbarDesktop.syncCalendar" />
          ) : (
            <FormattedMessage id="TopbarDesktop.addExperience" />
          )}
        </span>
      </NamedLink>
    ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showManageListingsLink}
      showCreateListingsLink={showCreateListingsLink}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <div className={classNames(css.topbarLinks, css.topbarLinksLeft)}>
        <TopbarNavLink name="LandingPage" currentPage={currentPage} pageName="LandingPage">
          <FormattedMessage id="Topbar.nav.home" />
        </TopbarNavLink>
        <TopbarNavLink name="SearchPage" currentPage={currentPage} pageName="SearchPage">
          <FormattedMessage id="Topbar.nav.discover" />
        </TopbarNavLink>
        <TopbarNavLink
          name="CMSPage"
          params={{ pageId: 'team' }}
          currentPage={currentPage}
          pageName="CMSPage"
        >
          <FormattedMessage id="Topbar.nav.team" />
        </TopbarNavLink>
      </div>
      <LinkedLogo
        id="logo-topbar-desktop"
        // className={css.logoLink}
        className={classNames(css.logoLink, css.centerLogo, {
          [css.centerLogoSearchPage]: isSearchPage(currentPage),
        })}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      <div className={classNames(css.topbarLinks, css.topbarLinksRight)}>
        <TopbarNavLink
          name="CMSPage"
          params={{ pageId: 'media' }}
          currentPage={currentPage}
          pageName="CMSPage"
        >
          <FormattedMessage id="Topbar.nav.media" />
        </TopbarNavLink>
        <button
          type="button"
          className={classNames(css.navLink, css.navLinkButton)}
          onClick={onSearchClick}
        >
          <FormattedMessage id="Topbar.nav.search" />
        </button>
        <TopbarNavLink
          name="PrivateAccessPage"
          currentPage={currentPage}
          pageName="PrivateAccessPage"
        >
          <FormattedMessage id="Topbar.nav.privateAccess" />
        </TopbarNavLink>
        <LanguageMenu />
        {inboxLinkMaybe}
        {/* {createListingLinkMaybe} */}
        {profileMenuMaybe}
        {signupLinkMaybe}
        {loginLinkMaybe}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
